/**
 * The typed edge between `/sim` and `/content`.
 *
 * `/content` is JSON. This file imports it, checks every file against the
 * JSON Schema that sits beside it in `/content/schema`, checks that ids
 * point at things that exist, and hands the rest of the sim typed values.
 * Nothing downstream touches raw JSON.
 *
 * Throws rather than coercing: content that does not match is an authoring
 * bug, and failing at startup is cheaper than a world that generates wrong
 * three hours in (§19).
 */

import canonJson from '../content/canon/the-last-week.json' with { type: 'json' };
import chargenJson from '../content/chargen/chargen.json' with { type: 'json' };
import jobsJson from '../content/block/jobs.json' with { type: 'json' };
import locationsJson from '../content/block/locations.json' with { type: 'json' };
import manifestJson from '../content/manifest.json' with { type: 'json' };
import mapJson from '../content/block/map.json' with { type: 'json' };
import npcsJson from '../content/block/npcs.json' with { type: 'json' };
import openingJson from '../content/opening/problems.json' with { type: 'json' };
import canonSchema from '../content/schema/canon.schema.json' with { type: 'json' };
import chargenSchema from '../content/schema/chargen.schema.json' with { type: 'json' };
import commonSchema from '../content/schema/common.schema.json' with { type: 'json' };
import jobSchema from '../content/schema/job.schema.json' with { type: 'json' };
import locationSchema from '../content/schema/location.schema.json' with { type: 'json' };
import manifestSchema from '../content/schema/manifest.schema.json' with { type: 'json' };
import mapSchema from '../content/schema/map.schema.json' with { type: 'json' };
import npcSchema from '../content/schema/npc.schema.json' with { type: 'json' };
import openingSchema from '../content/schema/opening.schema.json' with { type: 'json' };
import { assertValid, type JsonSchema, type SchemaRegistry } from './schema.js';
import type {
  DayOfWeek,
  EmotionalState,
  Flaw,
  LocationKind,
  Recruitment,
  RelationshipAxes,
  StatName,
  StatValue,
  Stats,
  VoiceBlock,
} from './types/index.js';

/** The content version this build of the sim knows how to read. */
export const SUPPORTED_CONTENT_VERSION = 2;

// ---------------------------------------------------------------------------
// Card shapes: what the JSON looks like after validation. Ids are plain
// strings here; branding happens in `sim/generate.ts`.
// ---------------------------------------------------------------------------

export interface ContentManifest {
  readonly contentVersion: number;
  readonly title: string;
  readonly districtNames: readonly string[];
  readonly start: { readonly day: number; readonly time: string };
  readonly block: {
    readonly name: string;
    readonly lodging: string;
    readonly startAt: string;
    readonly knownAtStart: readonly string[];
  };
}

export interface LocationCard {
  readonly id: string;
  readonly name: string;
  readonly kind: LocationKind;
  readonly description: string;
  readonly entrance: { readonly x: number; readonly y: number };
  readonly phone: boolean;
  readonly owner: string | null;
  readonly warning?: string;
}

export interface RoutineSlotCard {
  readonly days: readonly DayOfWeek[];
  readonly from: string;
  readonly to: string;
  readonly at: string;
}

export interface NpcCard {
  readonly id: string;
  readonly name: string;
  readonly age: number;
  readonly role: string;
  readonly faction: string | null;
  readonly voice: VoiceBlock;
  readonly deceptionCapacity: number;
  readonly stats: Stats;
  readonly flaw: Flaw;
  readonly wants: readonly string[];
  readonly bonds: readonly string[];
  readonly recruitment: Recruitment;
  readonly routine: readonly RoutineSlotCard[];
  readonly reachableAt: readonly string[];
  readonly money: number;
  readonly mood: EmotionalState;
  readonly stances: readonly { readonly npc: string; readonly disposition: number }[];
  readonly secret: { readonly id: string; readonly content: string; readonly consequencesIfExposed: string };
  readonly dependent?: { readonly relation: string; readonly rollLine: string; readonly relationship: Readonly<Partial<RelationshipAxes>> };
}

export interface JobCard {
  readonly id: string;
  readonly name: string;
  readonly kind: 'legal' | 'illegal';
  readonly employer: string;
  readonly at: string;
  readonly days: readonly DayOfWeek[];
  readonly window: { readonly from: string; readonly to: string };
  readonly minutes: number;
  readonly pay: number;
  readonly requires: { readonly stats: Readonly<Partial<Record<StatName, StatValue>>>; readonly trust: number };
  readonly description: string;
}

export type ChargenEffect =
  | { readonly kind: 'stat'; readonly stat: StatName; readonly delta: number }
  | { readonly kind: 'relationship'; readonly npc: string; readonly axes: Readonly<Partial<RelationshipAxes>> }
  | { readonly kind: 'money'; readonly amount: number }
  | { readonly kind: 'known_location'; readonly location: string }
  | { readonly kind: 'obligation'; readonly to: string; readonly amount: number; readonly deadlineDays: number }
  | { readonly kind: 'secret'; readonly id: string; readonly content: string; readonly consequencesIfExposed: string };

export interface FlawDef {
  readonly id: string;
  readonly name: string;
  readonly detail: string;
  readonly value: number;
  readonly rollLine: string;
  readonly effects: readonly ChargenEffect[];
}

export interface StrengthDef {
  readonly id: string;
  readonly kind: 'stat' | 'relationship' | 'connection' | 'money';
  readonly name: string;
  readonly cost: number;
  readonly repeatable?: boolean;
  readonly rollLine: string;
  readonly effects: readonly ChargenEffect[];
}

export interface QuestionOption {
  readonly id: string;
  readonly text: string;
  readonly flaws: readonly string[];
  readonly strengths: readonly string[];
  readonly dependent?: string;
}

export interface Question {
  readonly id: string;
  readonly prompt: string;
  readonly options: readonly QuestionOption[];
}

export interface ChargenData {
  readonly baseStats: Stats;
  readonly startingMoney: number;
  readonly randomRollBonus: number;
  readonly randomRollFlaws: { readonly min: number; readonly max: number };
  readonly surname: string;
  readonly firstNames: readonly string[];
  readonly flaws: readonly FlawDef[];
  readonly strengths: readonly StrengthDef[];
  readonly questions: readonly Question[];
}

export interface OpeningTemplate {
  readonly id: string;
  readonly dependent: string;
  readonly owedTo: string;
  readonly need: string;
  readonly deadlineDays: { readonly min: number; readonly max: number };
  readonly deadlineHour: number;
  readonly failureOpens: string;
  readonly opening: readonly string[];
}

export interface OpeningData {
  readonly weights: { readonly clean: number; readonly dirty: number; readonly none: number };
  readonly problems: readonly OpeningTemplate[];
}

/** The structured canon (§3). Loaded, never narrated, not generated from in V0. */
export interface CanonSeeded {
  readonly key: string;
  readonly options: readonly { readonly id: string; readonly text: string; readonly weight?: number }[];
}

export interface CanonRecord {
  readonly crew: {
    readonly name: string;
    readonly yearsRunning: number;
    readonly note: string;
    readonly people: readonly { readonly id: string; readonly name: string; readonly age: number; readonly role: string; readonly note: string }[];
    readonly associates: string;
  };
  readonly frame: readonly string[];
  readonly timeline: readonly {
    readonly id: string;
    readonly when: string;
    readonly title: string;
    readonly facts: readonly string[];
    readonly seeded?: readonly CanonSeeded[];
  }[];
  readonly whereabouts: readonly { readonly person: string; readonly facts: readonly string[]; readonly seeded?: CanonSeeded }[];
  readonly tiers: readonly { readonly tier: number; readonly name: string; readonly cost: string; readonly facts: readonly string[]; readonly reachableThrough?: readonly string[] }[];
  readonly positions: readonly { readonly who: string; readonly position: string }[];
  readonly consequences: readonly string[];
  readonly returnConditions: readonly string[];
  readonly distortionRules: readonly string[];
}

/**
 * The district drawn (§16): SVG path data in map units. One unit is about a
 * minute's walk and matches `Location.entrance`. Buildings are keyed by
 * location so the sim never needs to know a shape, only a door.
 */
export interface MapData {
  readonly bounds: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
  readonly water: readonly string[];
  readonly rail: readonly string[];
  readonly streets: readonly { readonly name: string; readonly path: string; readonly labelAt: { readonly x: number; readonly y: number; readonly angle: number } }[];
  readonly buildings: readonly { readonly location: string; readonly path: string; readonly labelAt: { readonly x: number; readonly y: number }; readonly label?: string }[];
  readonly filler: readonly string[];
}

export interface Content {
  readonly manifest: ContentManifest;
  readonly map: MapData;
  readonly locations: readonly LocationCard[];
  readonly npcs: readonly NpcCard[];
  readonly jobs: readonly JobCard[];
  readonly chargen: ChargenData;
  readonly opening: OpeningData;
  readonly canon: CanonRecord;
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

const SCHEMAS: SchemaRegistry = Object.fromEntries(
  [commonSchema, npcSchema, locationSchema, jobSchema, chargenSchema, openingSchema, canonSchema, manifestSchema, mapSchema].map(
    (s) => [(s as JsonSchema).$id as string, s as JsonSchema],
  ),
);

/** The schema registry, for tests that want to validate their own data. */
export function contentSchemas(): SchemaRegistry {
  return SCHEMAS;
}

/** "HH:MM" to minute of day. "24:00" is 1440, an exclusive end. */
export function parseClock(clock: string): number {
  const [h, m] = clock.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function fail(problems: string[]): never {
  throw new Error(`Content is inconsistent:\n${problems.map((p) => `  ${p}`).join('\n')}`);
}

/** Every id must point at something. Schemas can't check that; this does. */
function crossCheck(content: Content): void {
  const problems: string[] = [];
  const locationIds = new Set(content.locations.map((l) => l.id));
  const npcIds = new Set(content.npcs.map((n) => n.id));
  const flawIds = new Set(content.chargen.flaws.map((f) => f.id));
  const strengthIds = new Set(content.chargen.strengths.map((s) => s.id));
  const need = (set: Set<string>, id: string, where: string) => {
    if (!set.has(id)) problems.push(`${where} refers to unknown id "${id}"`);
  };

  const m = content.manifest;
  need(locationIds, m.block.lodging, 'manifest.block.lodging');
  need(locationIds, m.block.startAt, 'manifest.block.startAt');
  m.block.knownAtStart.forEach((id) => need(locationIds, id, 'manifest.block.knownAtStart'));

  for (const l of content.locations) {
    if (l.owner !== null) need(npcIds, l.owner, `location ${l.id}.owner`);
    if (!content.map.buildings.some((b) => b.location === l.id)) problems.push(`location ${l.id} has no footprint on the map`);
    const b = content.map.bounds;
    if (l.entrance.x < b.x || l.entrance.x > b.x + b.width || l.entrance.y < b.y || l.entrance.y > b.y + b.height) {
      problems.push(`location ${l.id} entrance is outside the map bounds`);
    }
  }
  for (const b of content.map.buildings) need(locationIds, b.location, 'map.buildings');
  for (const n of content.npcs) {
    n.bonds.forEach((id) => need(npcIds, id, `npc ${n.id}.bonds`));
    n.stances.forEach((s) => need(npcIds, s.npc, `npc ${n.id}.stances`));
    n.routine.forEach((r) => {
      need(locationIds, r.at, `npc ${n.id}.routine`);
      if (parseClock(r.from) >= parseClock(r.to)) problems.push(`npc ${n.id} routine slot ${r.from}-${r.to} is empty`);
    });
    n.reachableAt.forEach((id) => need(locationIds, id, `npc ${n.id}.reachableAt`));
    if (n.recruitment.kind === 'conditional') {
      for (const c of n.recruitment.requires) if (c.kind === 'vouch') need(npcIds, c.by, `npc ${n.id}.recruitment`);
    }
  }
  const secretIds = content.npcs.map((n) => n.secret.id);
  if (new Set(secretIds).size !== secretIds.length) problems.push('secret ids are not unique');

  for (const j of content.jobs) {
    need(npcIds, j.employer, `job ${j.id}.employer`);
    need(locationIds, j.at, `job ${j.id}.at`);
    if (parseClock(j.window.from) >= parseClock(j.window.to)) problems.push(`job ${j.id} window is empty`);
  }

  const checkEffects = (effects: readonly ChargenEffect[], where: string) => {
    for (const e of effects) {
      if (e.kind === 'relationship' || e.kind === 'obligation') need(npcIds, e.kind === 'relationship' ? e.npc : e.to, where);
      if (e.kind === 'known_location') need(locationIds, e.location, where);
    }
  };
  content.chargen.flaws.forEach((f) => checkEffects(f.effects, `flaw ${f.id}`));
  content.chargen.strengths.forEach((s) => checkEffects(s.effects, `strength ${s.id}`));
  if (new Set(content.chargen.flaws.map((f) => f.id)).size !== content.chargen.flaws.length) problems.push('flaw ids are not unique');
  if (new Set(content.chargen.strengths.map((s) => s.id)).size !== content.chargen.strengths.length) problems.push('strength ids are not unique');

  const dependentCapable = new Set(content.npcs.filter((n) => n.dependent).map((n) => n.id));
  let dependentQuestions = 0;
  for (const q of content.chargen.questions) {
    const setsDependent = q.options.filter((o) => o.dependent !== undefined).length;
    if (setsDependent > 0) {
      dependentQuestions += 1;
      if (setsDependent !== q.options.length) problems.push(`question ${q.id}: every option must set a dependent, or none`);
    }
    for (const o of q.options) {
      o.flaws.forEach((id) => need(flawIds, id, `option ${q.id}.${o.id}.flaws`));
      o.strengths.forEach((id) => need(strengthIds, id, `option ${q.id}.${o.id}.strengths`));
      if (o.dependent !== undefined) need(dependentCapable, o.dependent, `option ${q.id}.${o.id}.dependent`);
    }
  }
  if (dependentQuestions !== 1) problems.push(`exactly one question must choose the dependent (found ${dependentQuestions})`);
  if (dependentCapable.size === 0) problems.push('no NPC card can be the dependent');

  for (const p of content.opening.problems) {
    need(dependentCapable, p.dependent, `opening ${p.id}.dependent`);
    need(npcIds, p.owedTo, `opening ${p.id}.owedTo`);
    need(npcIds, p.failureOpens, `opening ${p.id}.failureOpens`);
    if (p.deadlineDays.min > p.deadlineDays.max) problems.push(`opening ${p.id}: deadlineDays.min > max`);
  }
  for (const id of dependentCapable) {
    if (!content.opening.problems.some((p) => p.dependent === id)) problems.push(`dependent ${id} has no opening problem`);
  }

  // The canon's people are V1 (§20). They must not collide with the block.
  for (const person of content.canon.crew.people) {
    if (npcIds.has(person.id)) problems.push(`canon person "${person.id}" collides with a V0 NPC id`);
  }

  if (problems.length > 0) fail(problems);
}

let cached: Content | null = null;

/**
 * Load and validate everything in `/content`. Memoized: content is immutable
 * for the life of the process and validation is not free.
 */
export function loadContent(): Content {
  if (cached) return cached;

  assertValid(manifestJson, SCHEMAS['manifest.schema.json']!, SCHEMAS, 'content/manifest.json');
  const manifest = manifestJson as unknown as ContentManifest;
  if (manifest.contentVersion !== SUPPORTED_CONTENT_VERSION) {
    throw new Error(
      `Content version ${manifest.contentVersion} is not readable by this sim (expected ${SUPPORTED_CONTENT_VERSION}).`,
    );
  }

  const each = (items: unknown, schema: JsonSchema, what: string) => {
    if (!Array.isArray(items)) throw new Error(`${what} must be a list`);
    items.forEach((item, i) => assertValid(item, schema, SCHEMAS, `${what}[${i}]`));
  };
  each(locationsJson, SCHEMAS['location.schema.json']!, 'content/block/locations.json');
  each(npcsJson, SCHEMAS['npc.schema.json']!, 'content/block/npcs.json');
  each(jobsJson, SCHEMAS['job.schema.json']!, 'content/block/jobs.json');
  assertValid(chargenJson, SCHEMAS['chargen.schema.json']!, SCHEMAS, 'content/chargen/chargen.json');
  assertValid(openingJson, SCHEMAS['opening.schema.json']!, SCHEMAS, 'content/opening/problems.json');
  assertValid(canonJson, SCHEMAS['canon.schema.json']!, SCHEMAS, 'content/canon/the-last-week.json');
  assertValid(mapJson, SCHEMAS['map.schema.json']!, SCHEMAS, 'content/block/map.json');

  const content: Content = {
    manifest,
    map: mapJson as unknown as MapData,
    locations: locationsJson as unknown as LocationCard[],
    npcs: npcsJson as unknown as NpcCard[],
    jobs: jobsJson as unknown as JobCard[],
    chargen: chargenJson as unknown as ChargenData,
    opening: openingJson as unknown as OpeningData,
    canon: canonJson as unknown as CanonRecord,
  };
  crossCheck(content);
  cached = content;
  return content;
}
